import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ChatLastRequest, ChatSliceState, Message } from './types'
import { createStorage } from './storage'
import { TypeMappingRestOutput } from '@shared/api/middleware/mappingAPI/restaurant/type'

const LOCAL_STORAGE_KEY = 'gig-assist-messages'
const storage = createStorage(LOCAL_STORAGE_KEY)

export const BUTTON_OPENER_CONST = {
  DEFAULT_TEXT: 'Спросить ассистента...',
  ALT_TEXT: ['Где есть морепродукты?', 'Куда сходить на свидание?', 'Где самая вкусная пицца?'],
  MAX_PLAYED_COUNT: 2,
  DELAY_BEFORE_ANIMATION: 1000 * 60 * 3, // 3 минуты
  ANIMATION_STEP_DURATION: 1000 * 3, // 3 секунды
}

type ReceiveMessagePayload = {
  text: string
  tag?: string
  restaurants?: TypeMappingRestOutput[]
  uuid?: string
}

const initialState: ChatSliceState = {
  chatOpen: false,
  state: 'stable',
  messages: storage.load(),
  error: null,
  shouldFocusInput: false,
  uuid: null,
  messageTemplate: '{userMsg}',
  isTemplateEditorOpen: false,
  lastRequest: {
    requestText: null,
    responseText: null,
    status: null,
  },
  buttonOpener: {
    restVisited: false,
    showBubble: false,
    buttonText: BUTTON_OPENER_CONST.DEFAULT_TEXT,
    animationIndex: 0,
    playedCount: 0,
  },
}

const chatSlice = createSlice({
  name: 'gigAssist',
  initialState,
  reducers: {
    // * Отправка сообщения *
    sendMessage: (state, action: PayloadAction<string>) => {
      const msg: Message = {
        id: Date.now().toString(),
        text: action.payload,
        isBot: false,
        timestamp: new Date().toISOString(),
      }

      state.messages.push(msg)

      storage.save(state.messages)

      state.state = 'sending'
    },

    // * Получение ответа *
    receiveMessage: (state, action: PayloadAction<ReceiveMessagePayload>) => {
      // console.log('!!! receiveMessage action.payload', action.payload)

      const msg: Message = {
        id: Date.now().toString(),
        text: action.payload.text,
        isBot: true,
        timestamp: new Date().toISOString(),
        tag: action.payload.tag,
        restaurants: action.payload.restaurants,
      }

      // console.log('!!! receiveMessage msg', msg)

      state.messages.push(msg)

      // сохранение UUID с state
      if (action.payload.uuid) {
        state.uuid = action.payload.uuid
      }

      // console.log('!!! receiveMessage state.messages before save', state.messages)

      storage.save(state.messages)

      state.state = 'stable'
      state.shouldFocusInput = true
    },

    // * Ошибка отправки *
    setError: (state, action: PayloadAction<string>) => {
      if (state.state === 'canceled') return
      state.error = action.payload
      state.state = 'confused'
    },

    // * Ожидание ответа *
    setWaiting: (state) => {
      state.state = 'waiting'
    },

    // * Оффлайн режим *
    setOffline: (state) => {
      state.state = 'offline'
    },

    // * Успешное завершение *
    setSuccess: (state) => {
      state.state = 'success'
    },

    // * Сброс сообщений *
    resetMessages: (state) => {
      state.messages = []
      state.uuid = null
      storage.remove()
      state.state = 'stable'
      state.shouldFocusInput = true
    },

    // * Сброс фокуса *
    resetFocusInput: (state) => {
      state.shouldFocusInput = false
    },

    // * Отмена запроса *
    cancelRequest: (state) => {
      state.state = 'canceled'
    },

    // * Обновление шаблона сообщения *
    updateMessageTemplate: (state, action: PayloadAction<string>) => {
      state.messageTemplate = action.payload
    },

    // * Переключение редактора шаблона *
    toggleTemplateEditor: (state) => {
      state.isTemplateEditorOpen = !state.isTemplateEditorOpen
    },

    // * Переключение видимости чата *
    setChatOpen: (state, action: PayloadAction<boolean>) => {
      state.chatOpen = action.payload
    },

    // BUTTON OPENER
    buttonOpenerInit: (state) => {
      state.buttonOpener.restVisited = localStorage.getItem('visitedProductForChatButton') === 'true'
      state.buttonOpener.playedCount = Number(localStorage.getItem('chatButtonPlayedCount')) || 0
    },
    buttonOpenerResetToDefault: (state) => {
      state.buttonOpener.showBubble = false
      state.buttonOpener.buttonText = BUTTON_OPENER_CONST.DEFAULT_TEXT
      state.buttonOpener.animationIndex = 0
    },
    buttonOpenerPlayScenario: (state) => {
      state.buttonOpener.showBubble = true
      state.buttonOpener.animationIndex = 0
      state.buttonOpener.buttonText = BUTTON_OPENER_CONST.ALT_TEXT[0]
    },
    buttonOpenerSetAnimationIndex: (state, action: PayloadAction<number>) => {
      const idx = action.payload
      state.buttonOpener.animationIndex = idx
      state.buttonOpener.buttonText = BUTTON_OPENER_CONST.ALT_TEXT[idx] || BUTTON_OPENER_CONST.DEFAULT_TEXT
    },
    buttonOpenerFinishScenario: (state) => {
      state.buttonOpener.showBubble = false
      state.buttonOpener.buttonText = BUTTON_OPENER_CONST.DEFAULT_TEXT
      state.buttonOpener.animationIndex = 0
      state.buttonOpener.playedCount += 1
      localStorage.setItem('chatButtonPlayedCount', String(state.buttonOpener.playedCount))
    },
    buttonOpenerSetRestVisited: (state, action: PayloadAction<boolean>) => {
      state.buttonOpener.restVisited = action.payload
    },

    setLastRequestData: (state, action: PayloadAction<ChatLastRequest>) => {
      state.lastRequest = action.payload
    },
  },
})

export const {
  setChatOpen,
  sendMessage,
  receiveMessage,
  setError,
  setWaiting,
  setOffline,
  setSuccess,
  resetMessages,
  resetFocusInput,
  cancelRequest,
  updateMessageTemplate,
  toggleTemplateEditor,

  buttonOpenerInit,
  buttonOpenerResetToDefault,
  buttonOpenerPlayScenario,
  buttonOpenerSetAnimationIndex,
  buttonOpenerFinishScenario,
  buttonOpenerSetRestVisited,

  setLastRequestData,
} = chatSlice.actions

export default chatSlice.reducer
